import type {
  Service,
  Testimonial,
  GalleryImage,
  CompanyInfo,
  Enquiry,
  ContactMessage,
  ApiResponse,
  ApiError
} from './types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://securitybackend.magnytesolution.com';
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nfc.arbotix.online';
const API_PREFIX = '/api/manage_data';

// Token management
const TOKEN_KEY = 'auth_token';

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Generic API client
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, config);
      const data = await response.json();

      if (!response.ok) {
        throw data as ApiError;
      }

      return data as T;
    } catch (error) {
      if (error instanceof Error) {
        throw {
          status: false,
          message: error.message,
        } as ApiError;
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

const apiClient = new ApiClient(API_BASE_URL);

// Service API
export const serviceApi = {
  getAll: (params?: { is_active?: string; category?: string }) =>
    apiClient.get<ApiResponse<Service[]>>(`${API_PREFIX}/Service/`, params),

  getById: (id: number) =>
    apiClient.get<ApiResponse<Service>>(`${API_PREFIX}/Service/${id}/`),

  getBySlug: (slug: string) =>
    apiClient.get<ApiResponse<Service[]>>(`${API_PREFIX}/Service/`, { slug }),

  create: (data: Partial<Service>) =>
    apiClient.post<ApiResponse<Service>>(`${API_PREFIX}/Service/`, data),

  update: (id: number, data: Partial<Service>) =>
    apiClient.put<ApiResponse<Service>>(`${API_PREFIX}/Service/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PREFIX}/Service/${id}/`),
};

// Testimonial API
export const testimonialApi = {
  getAll: (params?: { is_active?: string; is_featured?: string }) =>
    apiClient.get<ApiResponse<Testimonial[]>>(`${API_PREFIX}/Testimonial/`, params),

  getById: (id: number) =>
    apiClient.get<ApiResponse<Testimonial>>(`${API_PREFIX}/Testimonial/${id}/`),

  create: (data: Partial<Testimonial>) =>
    apiClient.post<ApiResponse<Testimonial>>(`${API_PREFIX}/Testimonial/`, data),

  update: (id: number, data: Partial<Testimonial>) =>
    apiClient.put<ApiResponse<Testimonial>>(`${API_PREFIX}/Testimonial/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PREFIX}/Testimonial/${id}/`),
};

// Gallery API
export const galleryApi = {
  getAll: (params?: { is_active?: string; category?: string }) =>
    apiClient.get<ApiResponse<GalleryImage[]>>(`${API_PREFIX}/GalleryImage/`, params),

  getById: (id: number) =>
    apiClient.get<ApiResponse<GalleryImage>>(`${API_PREFIX}/GalleryImage/${id}/`),

  create: (data: Partial<GalleryImage>) =>
    apiClient.post<ApiResponse<GalleryImage>>(`${API_PREFIX}/GalleryImage/`, data),

  update: (id: number, data: Partial<GalleryImage>) =>
    apiClient.put<ApiResponse<GalleryImage>>(`${API_PREFIX}/GalleryImage/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PREFIX}/GalleryImage/${id}/`),

  uploadImages: async (files: File[], category: string, isActive: boolean = true) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    formData.append('category', category);
    formData.append('is_active', isActive.toString());

    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/api/upload_gallery_images/`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw data as ApiError;
    }
    return data;
  },
};


// Company Info API
export const companyInfoApi = {
  get: () =>
    apiClient.get<ApiResponse<CompanyInfo[]>>(`${API_PREFIX}/CompanyInfo/`),

  update: (id: number, data: Partial<CompanyInfo>) =>
    apiClient.put<ApiResponse<CompanyInfo>>(`${API_PREFIX}/CompanyInfo/${id}/`, data),
};

// Enquiry API
export const enquiryApi = {
  getAll: (params?: { status?: string; service_type?: string }) =>
    apiClient.get<ApiResponse<Enquiry[]>>(`${API_PREFIX}/Enquiry/`, params),

  getById: (id: number) =>
    apiClient.get<ApiResponse<Enquiry>>(`${API_PREFIX}/Enquiry/${id}/`),

  create: (data: Partial<Enquiry>) =>
    apiClient.post<ApiResponse<Enquiry>>(`${API_PREFIX}/Enquiry/`, data),

  update: (id: number, data: Partial<Enquiry>) =>
    apiClient.put<ApiResponse<Enquiry>>(`${API_PREFIX}/Enquiry/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PREFIX}/Enquiry/${id}/`),
};

// Contact Message API
export const contactMessageApi = {
  getAll: (params?: { status?: string }) =>
    apiClient.get<ApiResponse<ContactMessage[]>>(`${API_PREFIX}/ContactMessage/`, params),

  getById: (id: number) =>
    apiClient.get<ApiResponse<ContactMessage>>(`${API_PREFIX}/ContactMessage/${id}/`),

  create: (data: Partial<ContactMessage>) =>
    apiClient.post<ApiResponse<ContactMessage>>(`${API_PREFIX}/ContactMessage/`, data),

  update: (id: number, data: Partial<ContactMessage>) =>
    apiClient.put<ApiResponse<ContactMessage>>(`${API_PREFIX}/ContactMessage/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PREFIX}/ContactMessage/${id}/`),
};

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post<{
      status: boolean;
      message: string;
      token: string;
      userData: unknown;
    }>('/api/login/', { user_email: email, password });

    if (response.status && response.token) {
      setToken(response.token);
    }

    return response;
  },

  logout: async () => {
    const token = getToken();
    if (token) {
      await apiClient.post('/api/logout/', { accessToken: token });
      removeToken();
    }
  },

  signup: async (data: {
    user_name: string;
    business_name: string;
    user_email: string;
    contact_number: string;
    password: string;
    address: string;
    city: string;
  }) => {
    return apiClient.post<{ status: boolean; message: string }>('/api/signup/', data);
  },

  forgotPassword: async (email: string) => {
    return apiClient.post<{ status: boolean; message: string }>('/api/forgot-password/', { user_email: email });
  },

  resetPassword: async (email: string, token: string, newPassword: string) => {
    return apiClient.post<{ status: boolean; message: string }>('/api/reset-password/', {
      user_email: email,
      token,
      new_password: newPassword
    });
  },
};

export default apiClient;